/**
 * WarehouseAccountDetails — read-only summary of a saved warehouse account.
 *
 * Renders a strict whitelist of non-secret fields per type. Secrets (passwords,
 * tokens, private keys) and generated artifacts (Setup SQL, public key,
 * fingerprint) are never rendered — using an explicit allowlist means they
 * cannot leak even if the redacted config were to carry a masked value.
 */
import { effectivePostgresSslMode } from '@server/postgres';
import type { WarehouseConnectionRedacted } from '@server/types';
import * as React from 'react';

type RedactedConfig = WarehouseConnectionRedacted['config'];

interface DetailRow {
  label: string;
  value: string | undefined;
}

const SNOWFLAKE_AUTH_LABELS: Record<string, string> = {
  password: 'Password',
  key_pair: 'Key pair',
  managed_key: 'Managed key pair',
};
const REDSHIFT_AUTH_LABELS: Record<string, string> = {
  password: 'Password',
  managed_password: 'Managed password',
};
const DATABRICKS_AUTH_LABELS: Record<string, string> = {
  pat: 'Personal access token',
  oauth_m2m: 'OAuth (M2M)',
};

/** Build the non-secret detail rows for a given warehouse type + redacted config. */
function buildRows(type: string, config: RedactedConfig): DetailRow[] {
  const upper = type.toUpperCase();

  if (upper === 'SNOWFLAKE' && config && 'account' in config) {
    const c = config;
    return [
      { label: 'Account', value: c.account },
      { label: 'Database', value: c.database },
      { label: 'Warehouse', value: c.warehouse },
      { label: 'Schema', value: c.schema },
      { label: 'Role', value: c.role },
      { label: 'Service username', value: c.username },
      { label: 'Authentication', value: SNOWFLAKE_AUTH_LABELS[c.authType] ?? c.authType },
    ];
  }

  if (
    upper === 'POSTGRES' &&
    config &&
    'host' in config &&
    'database' in config &&
    !('authType' in config) &&
    !('secure' in config)
  ) {
    const c = config;
    return [
      { label: 'Host', value: c.host },
      { label: 'Port', value: c.port ? String(c.port) : undefined },
      { label: 'Database', value: c.database },
      { label: 'User', value: c.user },
      { label: 'Namespace', value: c.schema },
      { label: 'SSL mode', value: effectivePostgresSslMode(c.sslmode) ?? 'require' },
    ];
  }

  if (
    (upper === 'RS' || upper === 'REDSHIFT') &&
    config &&
    'host' in config &&
    'authType' in config &&
    'database' in config
  ) {
    const c = config;
    return [
      { label: 'Host', value: c.host },
      { label: 'Port', value: c.port ? String(c.port) : undefined },
      { label: 'Database', value: c.database },
      { label: 'User', value: c.user },
      { label: 'Namespace', value: c.schema },
      { label: 'SSL mode', value: c.sslmode },
      { label: 'Authentication', value: REDSHIFT_AUTH_LABELS[c.authType] ?? c.authType },
    ];
  }

  if ((upper === 'BQ' || upper === 'BIGQUERY') && config && 'projectId' in config) {
    const c = config;
    return [
      { label: 'Project ID', value: c.projectId },
      { label: 'Location', value: c.location },
      { label: 'Service account', value: c.credentials?.client_email },
      { label: 'Max bytes billed', value: c.maxBytesBilled },
    ];
  }

  if ((upper === 'DATABRICKS' || upper === 'DELTALAKE') && config && 'httpPath' in config) {
    const c = config;
    return [
      { label: 'Host', value: c.host },
      { label: 'HTTP path', value: c.httpPath },
      { label: 'Catalog', value: c.catalog },
      { label: 'Schema', value: c.schema },
      { label: 'Authentication', value: DATABRICKS_AUTH_LABELS[c.authType] ?? c.authType },
      { label: 'OAuth client ID', value: c.authType === 'oauth_m2m' ? c.oauthClientId : undefined },
    ];
  }

  if (upper === 'CLICKHOUSE' && config && 'secure' in config && 'database' in config) {
    const c = config;
    return [
      { label: 'Host', value: c.host },
      { label: 'Port', value: c.port ? String(c.port) : undefined },
      { label: 'Default database', value: c.database },
      { label: 'Cluster', value: c.cluster },
      { label: 'User', value: c.user },
      { label: 'Secure', value: c.secure ? 'Yes' : 'No' },
    ];
  }

  return [];
}

export function WarehouseAccountDetails({ type, config }: { type: string; config: RedactedConfig }): React.JSX.Element {
  const rows = buildRows(type, config).filter((r) => r.value !== undefined && r.value !== '');

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">Connection details aren't available for this account.</p>;
  }

  return (
    <dl className="divide-y divide-grey-50 rounded-xl border border-grey-50 bg-grey-25">
      {rows.map((row) => (
        <div key={row.label} className="flex items-start justify-between gap-4 px-4 py-3">
          <dt className="w-[30%] shrink-0 text-sm text-grey-400">{row.label}</dt>
          <dd className="min-w-0 text-right text-sm font-medium wrap-break-word text-foreground">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
